import React, { useState, useEffect } from "react";
import { Form, Input, Button, Upload as AntUpload, Card, message } from "antd";
import { Upload, CheckCircle, FileText } from "lucide-react";
import axios from "../../../services/axios";
import { useLocation, useNavigate } from 'react-router-dom';

const UploadDocumentForm = () => {
  const location = useLocation();
  const [fileList, setFileList] = useState([]);
  const [isReupload, setIsReupload] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const projectName = JSON.parse(localStorage.getItem('Project'))        
  const user = localStorage.getItem('user');
  const [originalDocumentCode, setOriginalDocumentCode] = useState('');

  useEffect(() => {
  
      if (location.state?.prefillData) {
        const prefillData = location.state.prefillData;
        setIsReupload(true);
        setOriginalDocumentCode(prefillData.originalDocumentCode);
        form.setFieldsValue({
          documentReference: prefillData.documentReference,
          changeReference: prefillData.changeReference,
          documentDescription: prefillData.documentDescription,
          discipline: prefillData.discipline,
          wbsReference: prefillData.wbsReference,
          description: prefillData.description,
          vat: prefillData.vat,
          grossPayableAmount: prefillData.grossPayableAmount,
          amount: prefillData.amount,
          comment: prefillData.comment,
        });
      }
    }, [location.state, form]);

  const handleUpload = async (values) => {
    values.user = user;
    values.projectName = projectName;

    if (isReupload && originalDocumentCode) {
      values.code = originalDocumentCode;
    }
    
    if (fileList.length === 0) {
      message.warning("Please upload a file.");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", fileList[0]);
    const data = { ...values };
    formData.append("data", JSON.stringify(data));
    
    setLoading(true);
    try {
      if (isReupload) {
        await axios.post("/reupload-claims-document", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 600000
        } );
        message.success("File uploaded successfully!");
        form.resetFields();
        setFileList([]);
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 3000);
      } else {

        await axios.post("/upload-claims-document", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 600000,
        });
        message.success("File uploaded successfully!");
        form.resetFields();
        setFileList([]);
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 3000);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
       if (err.response && err.response.data && err.response.data.msg) {
              message.warning(err.response.data.msg);
            } else if (err.response && err.response.data && err.response.data.userName) {
              message.warning(err.response.data.userName[0]);
            } else if (err.response && err.response.status === 401) {
              message.error('User not authorized');
            } else {
              message.error('Something went wrong');
            }
    }
  };

  return (
    <div className="relative flex-1 p-5 overflow-y-auto ml-2 mr-2 min-h-screen">
      <div className="max-w-3xl mx-auto">
        {/* Heading */}
        {/* <h2 className="text-2xl font-semibold text-gray-800 flex items-center justify-center space-x-2 p-2">
          <FileText className="text-blue-500 w-6 h-6" />
          <span>Upload Document</span>
        </h2> */}

        <Card className="shadow-md rounded-lg p-6">
          <Form form={form} layout="vertical"
          onFinish={handleUpload}>
            <Form.Item
              label="Document Reference"
              name="documentReference"
              rules={[{ required: true, message: "Please enter document reference" }]}
            >
              <Input placeholder="Enter document reference." disabled={isReupload}/>
            </Form.Item>

            <Form.Item
              label="Change Reference"
              name="changeReference"
              rules={[{ required: true, message: "Please enter change reference" }]}
            >
              <Input placeholder="Enter change reference" disabled={isReupload}/>
            </Form.Item>

            <Form.Item
              label="Discipline"
              name="discipline"
              rules={[{ required: true, message: "Please enter discipline" }]}
            >
              <Input placeholder="Enter discipline" />
            </Form.Item>

            <Form.Item
              label="WBS Reference"
              name="wbsReference"
              rules={[{ required: true, message: "Please enter wbs reference" }]}
            >
              <Input placeholder="Enter wbs reference" />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: "Please enter description" }]}
            >
              <Input placeholder="Enter description" />
            </Form.Item>

            <Form.Item
              label="Amount"
              name="amount"
              rules={[{ required: true, message: "Please enter amount" }]}
            >
              <Input placeholder="Enter amount" type="number"/>
            </Form.Item>

            <Form.Item
              label="Comment"
              name="comment"
              rules={[{ required: true, message: "Please enter a comment" }]}
              >
              <Input.TextArea placeholder="Enter comment" rows={4} />
            </Form.Item>

            <Form.Item
              label="VAT @ 5%"
              name="vat"
              rules={[{ required: true, message: "Please enter vat @ 5%" }]}
            >
              <Input placeholder="Enter vat @ 5%" type="number"/>
            </Form.Item>

            <Form.Item
              label="Gross Payable Amount"
              name="grossPayableAmount"
              rules={[{ required: true, message: "Please enter grossPayableAmount" }]}
            >
              <Input placeholder="Enter grossPayableAmount" type="number"/>
            </Form.Item>

            {/* Upload Section */}
            <Form.Item
              label="Upload File"
              name="uploadFile"
              rules={[{ required: true, message: "Please upload a file" }]}
            >
              <AntUpload
                fileList={fileList}
                beforeUpload={(file) => {
                  setFileList([...fileList, file]);
                  return false; 
                }}
                onRemove={(file) => {
                  setFileList(fileList.filter((f) => f.uid !== file.uid));
                }}
              >
                <Button icon={<Upload className="w-4 h-4" />}>Upload File</Button>
              </AntUpload>
            </Form.Item>

            {/* Submit Button */}
            <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="mt-4 w-full sm:w-auto"
              loading={loading}
            >
              Upload Document
            </Button>
            </Form.Item>

            {uploadSuccess && (
              <div className="flex items-center gap-2 text-green-500 mt-4">
                <CheckCircle className="w-5 h-5" />
                <span>File uploaded successfully!</span>
              </div>
            )}
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default UploadDocumentForm;
